import {
    Badge,
    Box,
    Flex, Heading, Icon,
    SimpleGrid,
    SkeletonCircle,
    SkeletonText, Stat, StatHelpText, StatLabel, StatNumber, Table, TableContainer, Tbody, Td,
    Th, Thead, Tr
} from "@chakra-ui/react";
import React, {useEffect, useState} from "react";
import {getData} from "../../../services/api";
import Card from "../../../components/card/Card";
import {
    MdPerson,
    MdHome,
    MdEventAvailable,
} from "react-icons/md";
import {
    FaEye,
    FaEuroSign
} from "react-icons/fa";
import {Line, LineChart, ResponsiveContainer, Tooltip, XAxis} from "recharts";
import moment from "moment";

export default function Dashboard() {
    const [loading, setLoading] = useState('rentals');
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading('stats');
                const result = await getData('/api/stats');
                setStats(result.data);
                setLoading(null);
            } catch (error) {
                setError(error.message);
                setLoading(null);
            }
        };
        fetchData();
    }, []);

    return (
        <Box pt={"80px"}>
            {loading === 'stats' ? (
                <SimpleGrid columns={{base: 1, md: 3}} gap='20px'>
                    <Box padding='6' boxShadow='lg' bg='white'>
                        <SkeletonCircle size='10'/>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='2'/>
                    </Box>
                    <Box padding='6' boxShadow='lg' bg='white'>
                        <SkeletonCircle size='10'/>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='2'/>
                    </Box>
                    <Box padding='6' boxShadow='lg' bg='white'>
                        <SkeletonCircle size='10'/>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='2'/>
                    </Box>
                    <Box padding='6' boxShadow='lg' bg='white'>
                        <SkeletonCircle size='10'/>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='2'/>
                    </Box>
                    <Box padding='6' boxShadow='lg' bg='white'>
                        <SkeletonCircle size='10'/>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='2'/>
                    </Box>
                    <Box padding='6' boxShadow='lg' bg='white'>
                        <SkeletonCircle size='10'/>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='2'/>
                    </Box>
                </SimpleGrid>
            ) : error ? (
                <div>Error: {error}</div>
            ) : stats ? (
                <Box>
                    <SimpleGrid columns={{base: 1, md: 2}} gap='20px'>
                        <Card>
                            <Flex alignItems="center">
                                <Stat>
                                    <StatLabel>Inserate</StatLabel>
                                    <StatNumber>
                                        {stats.rentals.total}
                                    </StatNumber>
                                    <StatHelpText>Insgesamt</StatHelpText>
                                    <Flex gap='5px'>
                                        <Badge colorScheme='green'>{stats.rentals.online} inseriert</Badge>
                                        <Badge colorScheme='red'>{stats.rentals.offline} nicht inseriert</Badge>
                                        <Badge colorScheme='purple'>{stats.rentals.created} in Bearbeitung</Badge>
                                    </Flex>
                                </Stat>
                                <Icon as={MdHome} boxSize={10} color="gray.700"/>
                            </Flex>
                        </Card>
                        <Card>
                            <Flex alignItems="center">
                                <Stat>
                                    <StatLabel>Benutzer</StatLabel>
                                    <StatNumber>{stats.users.total}</StatNumber>
                                    <StatHelpText>Insgesamt</StatHelpText>
                                    <Flex gap='5px'>
                                        <Badge colorScheme='green'>{stats.users.has_rental} mit Inserat</Badge>
                                        <Badge colorScheme='red'>{stats.users.no_rental} ohne Inserat</Badge>
                                        <Badge colorScheme='purple'>{stats.users.unconfirmed} nicht bestätigt</Badge>
                                    </Flex>
                                </Stat>
                                <Icon as={MdPerson} boxSize={10} color="gray.700"/>
                            </Flex>
                        </Card>
                    </SimpleGrid>
                    <SimpleGrid columns={{base: 1, md: 3}} gap='20px' mt={5}>
                        <Card>
                            <Flex alignItems="center">
                                <Stat>
                                    <StatLabel>Buchungsanfragen</StatLabel>
                                    <StatNumber>{stats.booking_requests_total}</StatNumber>
                                    <StatHelpText>Insgesamt</StatHelpText>
                                </Stat>
                                <Icon as={MdEventAvailable} boxSize={10} color="gray.700"/>
                            </Flex>
                        </Card>
                        <Card>
                            <Flex alignItems="center">
                                <Stat>
                                    <StatLabel>Inserataufrufe</StatLabel>
                                    <StatNumber>{stats.rental_requests_total}</StatNumber>
                                    <StatHelpText>Insgesamt</StatHelpText>
                                </Stat>
                                <Icon as={FaEye} boxSize={10} color="gray.700"/>
                            </Flex>
                        </Card>
                        <Card>
                            <Flex alignItems="center">
                                <Stat>
                                    <StatLabel>Gesamtumsatz</StatLabel>
                                    <StatNumber>{stats.revenue_total}</StatNumber>
                                    <StatHelpText>Insgesamt</StatHelpText>
                                </Stat>
                                <Icon as={FaEuroSign} boxSize={10} color="gray.700"/>
                            </Flex>
                        </Card>
                    </SimpleGrid>
                    <Card mt={5}>
                        <Heading mb={3}>Inserate / Benutzer der letzten 30 Tage</Heading>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart width={400} height={300} data={stats.chart.daily}>
                                <XAxis hide={true} dataKey='name' />
                                <Tooltip/>
                                <Line type="monotone" name="Benutzer" dataKey="total_users" stroke="#8884d8" strokeWidth={2}/>
                                <Line type="monotone" name="Inserate" dataKey="total_rentals" stroke="#000" strokeWidth={2}/>
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>
                    <SimpleGrid mt={5} columns={{base: 1, md: 2}} gap='20px'>
                        <Card>
                            <Heading size='lg' mb={3}>Neueste Inserate</Heading>
                            <TableContainer>
                                <Table variant='simple'>
                                    <Thead>
                                        <Tr>
                                            <Th>ID</Th>
                                            <Th>Benutzer</Th>
                                            <Th isNumeric>Erstellt am</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {stats.last_rentals.length > 0 ? (
                                            stats.last_rentals.map((rental, index) => (
                                                <Tr key={index}>
                                                    <Td>{rental.uuid}</Td>
                                                    <Td>{rental.user?.email}</Td>
                                                    <Td>{moment(rental.created_at).format('DD.MM.YYYY')}</Td>
                                                </Tr>
                                            ))
                                        ) : (
                                            <Tr>
                                                <Td colSpan="4">Keine Daten gefunden</Td>
                                            </Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </Card>
                        <Card>
                            <Heading size='lg' mb={3}>Offene Rechnungen</Heading>
                            <TableContainer>
                                <Table variant='simple'>
                                    <Thead>
                                        <Tr>
                                            <Th>Nr.</Th>
                                            <Th>Email</Th>
                                            <Th>Inserat</Th>
                                            <Th isNumeric>Ausgestellt am</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {stats.last_invoices.length > 0 ? (
                                            stats.last_invoices.map((invoice, index) => (
                                                <Tr key={index}>
                                                    <Td>{invoice.uuid}</Td>
                                                    <Td>{invoice.user?.email}</Td>
                                                    <Td>{invoice.rental?.uuid}</Td>
                                                    <Td>{moment(invoice.created_at).format('DD.MM.YYYY')}</Td>
                                                </Tr>
                                            ))
                                        ) : (
                                            <Tr>
                                                <Td colSpan="4">Keine Daten gefunden</Td>
                                            </Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </Card>
                        <Card>
                            <Heading size='lg' mb={3}>Neueste Benutzer</Heading>
                            <TableContainer>
                                <Table variant='simple'>
                                    <Thead>
                                        <Tr>
                                            <Th>Name</Th>
                                            <Th>Email</Th>
                                            <Th isNumeric>Registriert am</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {stats.last_users.length > 0 ? (
                                            stats.last_users.map((user, index) => (
                                                <Tr key={index}>
                                                    <Td>{user.id}</Td>
                                                    <Td>{user.email}</Td>
                                                    <Td>{moment(user.created_at).format('DD.MM.YYYY')}</Td>
                                                </Tr>
                                            ))
                                        ) : (
                                            <Tr>
                                                <Td colSpan="4">Keine Daten gefunden</Td>
                                            </Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </Card>
                        <Card>
                            <Heading size='lg' mb={3}>Top 5 Seiten</Heading>
                            <TableContainer>
                                <Table variant='simple'>
                                    <Thead>
                                        <Tr>
                                            <Th>Name</Th>
                                            <Th>Inserate</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {stats.top_sources.length > 0 ? (
                                            stats.top_sources.map((source, index) => (
                                                <Tr key={index}>
                                                    <Td>{source.name}</Td>
                                                    <Td>{source.count}</Td>
                                                </Tr>
                                            ))
                                        ) : (
                                            <Tr>
                                                <Td colSpan="4">Keine Daten gefunden</Td>
                                            </Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </SimpleGrid>
                </Box>
            ) : (
                <div>Keine Daten gefunden</div>
            )}
        </Box>
    );
}
